import { useQuery } from '@tanstack/react-query';
import { fetchProductivitySummary } from '@apis/productivity';
import type { IProductivityCoverage, IProductivityMetric } from '@interfaces/IProductivity';

/** What the card reads off a site's productivity summary. */
export type ISiteProductivity = {
  /** The Productivity page's own headline card — value, tone and trend. */
  headline: IProductivityMetric | undefined;
  coverage: IProductivityCoverage;
};

/**
 * The site's all-time productivity (earned ÷ actual work days), read from the
 * same summary — under the same query key and freshness — as the site's
 * Productivity page, so the two can never show different figures. Fetched per
 * card after it renders: the summary is heavy and the portfolio never waits on
 * it. Not retried — a card without the figure is fine, a slow page is not.
 */
export function useSiteProductivity(siteId: string) {
  return useQuery({
    queryKey: ['productivity', 'summary', siteId],
    queryFn: ({ signal }) => fetchProductivitySummary(siteId, signal),
    select: (summary): ISiteProductivity => ({
      headline: summary.metrics.find((metric) => metric.id === 'project-productivity'),
      coverage: summary.coverage,
    }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
